// Product feedback. `submit` and `mine` are the tenant user's calls, `org` the
// tenant admin's; the rest
// are the SuperAdmin queue under /platform/feedback, which spans every tenant —
// so each passes `crossTenant` rather than inheriting a tenant scope the
// SuperAdmin happens to have picked elsewhere.

import apiService from "@/lib/api";

const FEEDBACK_BASE = "/api/v2/feedback";
const ADMIN_BASE = "/api/v2/platform/feedback";

const MULTIPART = { "Content-Type": "multipart/form-data" };

// The page and the filters that are set; an empty filter is left out.
const listParams = ({ page = 1, per_page = 10, type = "", status = "" } = {}) => ({
  page,
  per_page,
  ...(type && { type }),
  ...(status && { status }),
});

// Every screenshot goes under the one repeated key `images`; FastAPI drops a
// file sent under any other.
const feedbackForm = ({ type, comment, images = [] }) => {
  const formData = new FormData();
  formData.append("type", type);
  formData.append("comment", comment);
  images.forEach((file) => formData.append("images", file));
  return formData;
};

export const FeedbackService = {
  submit: async (values) => {
    const response = await apiService.postSecure(FEEDBACK_BASE, feedbackForm(values), { headers: MULTIPART });
    return response.data;
  },

  // A SuperAdmin imports one tenant's feedback from a CSV or Excel file. All
  // rows land or none do; a refusal lists each bad row in `data.errors`.
  importFile: async ({ tenantId, file }) => {
    const formData = new FormData();
    formData.append("tenant_id", tenantId);
    formData.append("file", file);
    const response = await apiService.postSecure(`${ADMIN_BASE}/import`, formData, { headers: MULTIPART, crossTenant: true });
    return response.data;
  },

  // Who new feedback is mailed to: { to, cc, bcc }, each a list of addresses.
  recipients: async () => {
    const response = await apiService.getSecure(`${ADMIN_BASE}/recipients`, { crossTenant: true });
    return response.data;
  },

  // Replaces all three lists at once.
  updateRecipients: async ({ to, cc, bcc }) => {
    const response = await apiService.putSecure(`${ADMIN_BASE}/recipients`, { to, cc, bcc }, { crossTenant: true });
    return response.data;
  },

  // The caller's own feedback. The server takes who they are from the token.
  mine: async (params) => {
    const response = await apiService.getSecure(`${FEEDBACK_BASE}/mine`, { params: listParams(params) });
    return response.data;
  },

  // Every feedback the caller's tenant sent, each with who sent it. Tenant
  // admins only; the server takes the tenant from the token.
  org: async (params) => {
    const response = await apiService.getSecure(`${FEEDBACK_BASE}/org`, { params: listParams(params) });
    return response.data;
  },

  // A screenshot on a row `mine` or `org` listed, as a blob — see fetchImage.
  fetchTenantImage: async (feedbackId, imageId) => {
    const response = await apiService.getSecure(`${FEEDBACK_BASE}/${feedbackId}/images/${imageId}`, { responseType: "blob" });
    return response.data;
  },

  list: async ({ page = 1, per_page = 10, status = "", type = "", tenant_id = "" } = {}) => {
    const params = { page, per_page };
    if (status) params.status = status;
    if (type) params.type = type;
    if (tenant_id) params.tenant_id = tenant_id;
    const response = await apiService.getSecure(ADMIN_BASE, { params, crossTenant: true });
    return response.data;
  },

  // A change that ends the feedback carries a reason: it is kept in the
  // feedback's history and quoted to the submitter. Any other change sends none.
  updateStatus: async (feedbackId, status, reason) => {
    const body = reason ? { status, reason } : { status };
    const response = await apiService.patch(`${ADMIN_BASE}/${feedbackId}/status`, body, { crossTenant: true });
    return response.data;
  },

  // The Excel sheet to fill in for an import, its Type column a dropdown.
  importTemplate: async () => {
    const response = await apiService.getSecure(`${ADMIN_BASE}/import/template`, { responseType: "blob", crossTenant: true });
    return response.data;
  },

  // Only while the feedback is Open; each item says so in `type_changeable`.
  updateType: async (feedbackId, type) => {
    const response = await apiService.patch(`${ADMIN_BASE}/${feedbackId}/type`, { type }, { crossTenant: true });
    return response.data;
  },

  history: async (feedbackId) => {
    const response = await apiService.getSecure(`${ADMIN_BASE}/${feedbackId}/history`, { crossTenant: true });
    return response.data;
  },

  // The bytes behind an <img>. An <img src> carries no Authorization header,
  // so the page fetches the blob and shows it through an object URL.
  fetchImage: async (feedbackId, imageId) => {
    const response = await apiService.getSecure(`${ADMIN_BASE}/${feedbackId}/images/${imageId}`, {
      responseType: "blob",
      crossTenant: true,
    });
    return response.data;
  },
};

export default FeedbackService;
